/**
 * 🆕 [Phase 30 #3b] HeuresBudgeteesBadge — 4 états visuels pour le tableau BT.
 *
 * États (par ordre de priorité) :
 *   1. BT marqué « À l'heure » (`a_l_heure: true`) → tiret gris « — »
 *   2. `officielles != null` → badge vert « Xh ✓ » (approuvé)
 *   3. `proposees != null && officielles == null` → badge amber « En attente »
 *   4. `proposees == null && officielles == null` → badge gris « Non proposé »
 *
 * Helper `getHeuresBudgeteesEtat(bon)` retourne :
 *   { etat: 'a_l_heure' | 'approuve' | 'en_attente' | 'non_propose',
 *     ordre: 0..3, valeur: number|null }
 * Utilisable pour le tri.
 */
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, AlertCircle, Minus } from 'lucide-react';

export function getHeuresBudgeteesEtat(bon) {
  if (!bon) return { etat: 'non_propose', ordre: 3, valeur: null };
  if (bon.a_l_heure) return { etat: 'a_l_heure', ordre: 0, valeur: null };
  const off = bon.heures_budgetees_officielles;
  const prop = bon.heures_budgetees_proposees;
  if (off !== null && off !== undefined && off !== 0) {
    return { etat: 'approuve', ordre: 1, valeur: Number(off) };
  }
  if (prop !== null && prop !== undefined && prop !== 0) {
    return { etat: 'en_attente', ordre: 2, valeur: Number(prop) };
  }
  return { etat: 'non_propose', ordre: 3, valeur: null };
}

export function HeuresBudgeteesBadge({ bon, className = '' }) {
  const { etat, valeur } = getHeuresBudgeteesEtat(bon);
  const testid = `heures-budgetees-badge-${etat}-${bon?.id || 'unknown'}`;

  if (etat === 'a_l_heure') {
    return (
      <span
        className={`inline-flex items-center gap-1 text-slate-400 text-xs ${className}`}
        title="BT marqué « À l'heure » — pas de budget fixe"
        data-testid={testid}
      >
        <Minus className="w-3 h-3" />
        <span>—</span>
      </span>
    );
  }

  if (etat === 'approuve') {
    return (
      <Badge
        variant="outline"
        className={`border-emerald-300 bg-emerald-50 text-emerald-700 text-xs flex items-center gap-1 ${className}`}
        title="Heures budgétées officiellement approuvées"
        data-testid={testid}
      >
        <CheckCircle2 className="w-3 h-3" />
        {valeur}h ✓
      </Badge>
    );
  }

  if (etat === 'en_attente') {
    return (
      <Badge
        variant="outline"
        className={`border-amber-300 bg-amber-50 text-amber-700 text-xs flex items-center gap-1 ${className}`}
        title="Heures proposées — en attente d'approbation owner"
        data-testid={testid}
      >
        <Clock className="w-3 h-3" />
        En attente ({valeur}h)
      </Badge>
    );
  }

  // non_propose
  return (
    <Badge
      variant="outline"
      className={`border-slate-300 bg-slate-50 text-slate-600 text-xs flex items-center gap-1 ${className}`}
      title="Aucune heure budgétée proposée"
      data-testid={testid}
    >
      <AlertCircle className="w-3 h-3" />
      Non proposé
    </Badge>
  );
}

export default HeuresBudgeteesBadge;
